import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useAttachCapacityComponentPointMutation,
  useDetachCapacityComponentPointMutation,
  useGetTSPsQuery,
} from '../services/edgeApi';
import { selectAllPoints, selectTsps } from '../map/slice/mapSelectors';
import { PointBadge } from '../helpers/PointBadge';
import { useCapacityComponents } from '../hooks/useCapacityComponents';
import { InfiniteScrollObserver } from '../helpers/InfiniteScrollObserver';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { useTheme } from '../helpers/hooks';
import { BooleanStatus } from '../helpers/BooleanStatus';
import { titleize } from '../utils/stringUtils';
import { BadgeDisplay } from '../helpers/BadgeDisplay';
import { CloseIcon, EditIcon, PlusIcon } from '@iconicicons/react';
import { PointSelectorModal } from '../points/PointSelectorModal';
import toast from 'react-hot-toast';
import { CapacityComponent } from '@/types';
import { EditCapacityComponentModal } from './EditCapacityComponentModal';

export const CapacityComponentTable = () => {
  useGetTSPsQuery(undefined);
  const [theme] = useTheme();
  const allPoints = useSelector(selectAllPoints);
  const tsps = useSelector(selectTsps);

  const [attachPointTrigger] = useAttachCapacityComponentPointMutation();
  const [detachPointTrigger] = useDetachCapacityComponentPointMutation();

  const { capacityComponents, isFetching, exhausted, next, refresh } =
    useCapacityComponents();

  const [selectedCapacityComponent, setSelectedCapacityComponent] =
    useState<CapacityComponent | null>(null);

  const [editCapacityComponent, setEditCapacityComponent] =
    useState<CapacityComponent | null>(null);

  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th>Component</th>
            <th>TSP</th>
            <th>Status</th>
            <th>Type</th>
            <th>Direction</th>
            <th>Primary Provider</th>
            <th>Primary Links</th>
            <th>Primary Flows (30d)</th>
            <th>Points</th>
            <th>Notice Components</th>
            {/* <th>Notice Activity</th> */}
          </tr>
        </thead>
        <tbody>
          {Object.values(capacityComponents ?? {}).map((component) => {
            const assigned = component.points.length > 0;
            const mapped = component.points.some((pointId) => {
              const point = allPoints[pointId];
              return point?.node !== null && point?.node !== undefined;
            });
            return (
              <tr key={component.id}>
                <td>
                  <EditIcon
                    className="me-3"
                    role="button"
                    onClick={() => setEditCapacityComponent(component)}
                  />
                  <b>#{component.id}</b> {component.label}
                </td>
                <td>{tsps[component.tsp_id]?.name}</td>
                <td>
                  {assigned ? (
                    <BooleanStatus
                      trueText="Mapped"
                      falseText="Not Mapped"
                      value={mapped}
                    />
                  ) : (
                    <BooleanStatus falseText="Not Assigned" value={false} />
                  )}
                </td>
                <td>{titleize(component.capacity_type)}</td>
                <td>{titleize(component.flow_direction)}</td>
                <td>{component.primary_flow_provider ?? '-'}</td>
                <td>
                  <BooleanStatus
                    value={Boolean(component.has_primary_provider_links)}
                  />
                </td>
                <td>
                  <BooleanStatus
                    value={Boolean(component.has_primary_provider_flows_30d)}
                  />
                </td>
                <td>
                  {component.points?.map((pointId) => {
                    const point = allPoints[pointId];
                    return (
                      <PointBadge
                        key={pointId}
                        point={point}
                        theme={theme}
                        extra_text={
                          <CloseIcon
                            role="button"
                            onClick={async () => {
                              if (
                                confirm(
                                  `Are you sure you want to remove ${point.name} from ${component.label}?`,
                                )
                              ) {
                                try {
                                  await detachPointTrigger({
                                    point,
                                    component,
                                  }).unwrap();
                                  toast.success('Point detached successfully');
                                } catch (error) {
                                  console.error(
                                    'Failed to detach point:',
                                    error,
                                  );
                                  toast.error(
                                    (error as any)?.data?.message ??
                                      'Failed to detach point',
                                  );
                                }
                                refresh();
                              }
                            }}
                          />
                        }
                      />
                    );
                  })}
                  <BadgeDisplay
                    role="button"
                    onClick={() => setSelectedCapacityComponent(component)}
                    theme={theme}
                  >
                    <PlusIcon />
                  </BadgeDisplay>
                </td>
                <td>
                  <BooleanStatus
                    value={
                      Object.values(component.notice_components ?? {}).length >
                      0
                    }
                  />
                </td>
                {/* <td>
                  <BooleanStatus value={component.has_recent_notices} />
                </td> */}
              </tr>
            );
          })}
        </tbody>
      </table>
      {isFetching && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '100px' }}
        >
          <LoadingIndicator />
        </div>
      )}
      {!exhausted && (
        <div className="d-flex justify-content-center">
          <InfiniteScrollObserver onScrollIn={next} />
        </div>
      )}
      <EditCapacityComponentModal
        component={editCapacityComponent}
        onClose={() => setEditCapacityComponent(null)}
        onSave={refresh}
      />
      <PointSelectorModal
        isOpen={selectedCapacityComponent !== null}
        onClose={() => setSelectedCapacityComponent(null)}
        size="lg"
        lockedTsps={
          selectedCapacityComponent?.tsp_id
            ? [selectedCapacityComponent.tsp_id]
            : []
        }
        onSelect={async (point) => {
          if (!selectedCapacityComponent) return;
          try {
            await attachPointTrigger({
              point: point,
              component: selectedCapacityComponent,
            }).unwrap();
            setSelectedCapacityComponent(null);
            toast.success('Point attached successfully');
          } catch (error) {
            console.error('Failed to attach point:', error);
            toast.error(
              (error as any)?.data?.message ?? 'Failed to attach point',
            );
          }
          refresh();
        }}
      />
    </>
  );
};
